<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import ConversationLabels from './labels/LabelBox.vue';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';

export default {
  components: {
    ConversationLabels,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      showPriorityDropdown: false,
      showTeamDropdown: false,
      priorityOptions: [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          thumbnail: `/assets/images/dashboard/priority/none.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.URGENT}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.HIGH}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.MEDIUM}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.LOW}.svg`,
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      teams: 'teams/getTeams',
    }),
    currentTeam() {
      const teamId = this.currentChat?.meta?.team?.id;
      if (!teamId) return null;
      return this.teams.find(t => t.id === teamId) || null;
    },
    currentTeamName() {
      return this.currentTeam ? this.currentTeam.name : 'Nenhum';
    },
    assignedPriority: {
      get() {
        const selectedOption = this.priorityOptions.find(
          opt => opt.id === this.currentChat.priority
        );
        return selectedOption || this.priorityOptions[0];
      },
      set(priorityItem) {
        const conversationId = this.currentChat.id;
        const oldValue = this.currentChat?.priority;
        const priority = priorityItem ? priorityItem.id : null;

        this.$store.dispatch('setCurrentChatPriority', {
          priority,
          conversationId,
        });
        this.$store
          .dispatch('assignPriority', { conversationId, priority })
          .then(() => {
            useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
              oldValue,
              newValue: priority,
              from: 'Conversation Sidebar',
            });
            useAlert(
              this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
                priority: priorityItem.name,
                conversationId,
              })
            );
          });
      },
    },
    priorityColorClass() {
      const p = this.assignedPriority?.id;
      const map = {
        urgent: 'bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-300',
        high: 'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-300',
        medium: 'bg-yellow-50 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-300',
        low: 'bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-300',
      };
      return map[p] || 'bg-n-slate-2 text-n-slate-11';
    },
  },
  methods: {
    onClickAssignPriority(selectedPriorityItem) {
      const isSamePriority =
        this.assignedPriority &&
        this.assignedPriority.id === selectedPriorityItem.id;
      this.assignedPriority = isSamePriority ? null : selectedPriorityItem;
      this.showPriorityDropdown = false;
    },
    togglePriorityDropdown() {
      this.showPriorityDropdown = !this.showPriorityDropdown;
    },
    closePriorityDropdown() {
      this.showPriorityDropdown = false;
    },
    toggleTeamDropdown() {
      this.showTeamDropdown = !this.showTeamDropdown;
    },
    closeTeamDropdown() {
      this.showTeamDropdown = false;
    },
    assignTeam(team) {
      const conversationId = this.currentChat.id;
      const teamId = team ? team.id : 0;
      this.$store
        .dispatch('assignTeam', { conversationId, teamId })
        .then(() => {
          useAlert(
            team
              ? `Time "${team.name}" atribuido com sucesso`
              : 'Time removido com sucesso'
          );
        })
        .catch(() => {
          useAlert('Erro ao atribuir time');
        });
      this.showTeamDropdown = false;
    },
  },
};
</script>

<template>
  <div>
    <!-- Etiquetas row -->
    <div class="border-t border-n-weak">
      <div class="flex flex-wrap items-center gap-1 py-2.5 px-4">
        <div class="flex items-center gap-2 shrink-0 mr-1">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="text-n-slate-10 shrink-0">
            <path d="M9 5H2v7l6.29 6.29c.94.94 2.48.94 3.42 0l3.58-3.58c.94-.94.94-2.48 0-3.42L9 5z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="6" cy="9" r="1" fill="currentColor"/>
            <path d="M15 5l6.3 6.3a2.4 2.4 0 010 3.4L17 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="text-sm font-medium text-n-slate-12">Etiquetas</span>
        </div>
        <ConversationLabels :conversation-id="conversationId" />
      </div>
    </div>

    <!-- Time row -->
    <div class="border-t border-n-weak">
      <div
        v-on-clickaway="closeTeamDropdown"
        class="relative flex items-center gap-2 py-2.5 px-4"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="text-n-slate-10 shrink-0">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="text-sm font-medium text-n-slate-12 shrink-0">Time</span>
        <button
          class="flex items-center gap-1 px-2 py-0.5 rounded-md text-sm cursor-pointer border-0 bg-n-slate-2 text-n-slate-11"
          @click="toggleTeamDropdown"
        >
          <span>{{ currentTeamName }}</span>
          <fluent-icon icon="chevron-down" size="12" />
        </button>
        <!-- Team dropdown -->
        <div
          v-if="showTeamDropdown"
          class="absolute left-0 top-full mt-1 w-56 border rounded-lg bg-n-alpha-3 backdrop-blur-[100px] shadow-lg border-n-strong p-1 z-[9999]"
        >
          <button
            class="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm text-n-slate-12 hover:bg-n-slate-3 cursor-pointer border-0 bg-transparent text-left"
            @click="assignTeam(null)"
          >
            <span>Nenhum</span>
          </button>
          <button
            v-for="team in teams"
            :key="team.id"
            class="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm text-n-slate-12 hover:bg-n-slate-3 cursor-pointer border-0 bg-transparent text-left"
            :class="{ 'bg-n-slate-3': currentTeam && currentTeam.id === team.id }"
            @click="assignTeam(team)"
          >
            <span>{{ team.name }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Prioridade row -->
    <div class="border-t border-n-weak">
      <div
        v-on-clickaway="closePriorityDropdown"
        class="relative flex items-center gap-2 py-2.5 px-4"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="text-n-slate-10 shrink-0">
          <path d="M7 14l5-5 5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M7 8l5-5 5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="text-sm font-medium text-n-slate-12 shrink-0">Prioridade</span>
        <button
          class="flex items-center gap-1 px-2 py-0.5 rounded-md text-sm cursor-pointer border-0"
          :class="priorityColorClass"
          @click="togglePriorityDropdown"
        >
          <img
            v-if="assignedPriority.thumbnail"
            :src="assignedPriority.thumbnail"
            class="w-3.5 h-3.5"
          />
          <span>{{ assignedPriority.name }}</span>
          <fluent-icon icon="chevron-down" size="12" />
        </button>
        <!-- Priority dropdown -->
        <div
          v-if="showPriorityDropdown"
          class="absolute left-0 top-full mt-1 w-48 border rounded-lg bg-n-alpha-3 backdrop-blur-[100px] shadow-lg border-n-strong p-1 z-[9999]"
        >
          <button
            v-for="option in priorityOptions"
            :key="option.id"
            class="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm text-n-slate-12 hover:bg-n-slate-3 cursor-pointer border-0 bg-transparent text-left"
            @click="onClickAssignPriority(option)"
          >
            <img
              v-if="option.thumbnail"
              :src="option.thumbnail"
              class="w-4 h-4"
            />
            <span>{{ option.name }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
